import { Base } from '@studiometa/js-toolkit';
import type { BaseProps } from '@studiometa/js-toolkit';
import { transform, lerp, map, easeInQuad, domScheduler } from '@studiometa/js-toolkit/utils';
import type { BaseConfig } from '@studiometa/js-toolkit';

export interface ScrollAnimationChildShowreelProps extends BaseProps {
  $refs: {
    target: HTMLElement;
    showreel: HTMLElement;
  };
}

/**
 * ScrollAnimationChildShowreel class.
 */
export default class ScrollAnimationChildShowreel extends Base<ScrollAnimationChildShowreelProps> {
  /**
   * Config.
   */
  static config: BaseConfig = {
    name: 'ScrollAnimationChildShowreel',
    refs: ['target', 'showreel'],
  };

  get origin() {
    return this.$root.$refs.showreelOrigin as HTMLElement;
  }

  get transformation() {
    const elRect = this.$el.getBoundingClientRect();
    const originRect = this.origin.getBoundingClientRect();

    const scaleX = originRect.width / elRect.width;
    const scaleY = originRect.height / elRect.height;

    const x = originRect.left + originRect.width / 2 - (elRect.left + elRect.width / 2);
    const y = originRect.top + originRect.height / 2 - (elRect.top + elRect.height / 2);

    return {
      scaleX,
      scaleY,
      x,
      y,
    };
  }

  mounted() {
    this.scrolledInView({ dampedProgress: { y: 0 } });
  }

  scrolledInView({ dampedProgress }) {
    domScheduler.read(() => {
      const { target, showreel } = this.$refs;
      const { x, y, scaleX, scaleY } = this.transformation;

      const mappedProgress = easeInQuad(map(Math.max(dampedProgress.y, 0.5), 0.5, 1, 0, 1));

      const targetBorderRadius = `max(16px, ${(1 - mappedProgress) * 25}vh)`;
      const showreelBorderRadius = `max(16px, ${(0.8 - mappedProgress) * 25}vh)`;

      const finalTransform = {
        x: lerp(x, 0, mappedProgress),
        y: lerp(y, 0, mappedProgress),
        scaleX: lerp(scaleX, 1, mappedProgress),
        scaleY: lerp(scaleY, 1, mappedProgress),
      };

      domScheduler.write(() => {
        transform(target, finalTransform);
        target.classList.remove('opacity-0');
        target.style.borderRadius = targetBorderRadius;
        showreel.style.borderRadius = showreelBorderRadius;
      });
    });
  }
}
